<template>
  <div class="campaigns-trend-chart">
    <!-- Chart Header -->
    <div class="chart-header">
      <div class="chart-title-section">
        <h3 class="chart-title">Campaigns</h3>
        <p class="chart-subtitle">Messages sent over time</p>
      </div>
      <div class="chart-controls">
        <div class="period-tabs">
          <button
            v-for="period in periods"
            :key="period.value"
            class="period-tab"
            :class="{ active: selectedPeriod === period.value }"
            @click="selectedPeriod = period.value"
          >
            {{ period.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Chart Body with SVG -->
    <div class="chart-body">
      <div class="chart-top-row">
        <div class="chart-summary">
          <div class="summary-main">
            <span class="summary-value">{{ totalSentThisWeek }}</span>
            <span v-if="weeklyTrend" class="summary-trend" :class="weeklyTrend > 0 ? 'trend-up' : 'trend-down'">
              <i :class="weeklyTrend > 0 ? 'fa fa-arrow-up' : 'fa fa-arrow-down'"></i>
              {{ Math.abs(weeklyTrend) }}%
            </span>
          </div>
          <span class="summary-label">Sent this week</span>
        </div>

        <!-- Stats inline with summary -->
        <div class="inline-stats">
          <div class="inline-stat">
            <span class="inline-stat-value">{{ activeCampaignsCount }}</span>
            <span class="inline-stat-label">Active</span>
          </div>
          <div class="inline-stat">
            <span class="inline-stat-value">{{ scheduledCampaignsCount }}</span>
            <span class="inline-stat-label">Scheduled</span>
          </div>
          <div class="inline-stat">
            <span class="inline-stat-value">{{ draftCampaignsCount }}</span>
            <span class="inline-stat-label">Drafts</span>
          </div>
        </div>
      </div>

      <!-- SVG Area Chart -->
      <div class="chart-container">
        <svg class="area-chart" viewBox="0 0 400 120" preserveAspectRatio="none">
          <!-- Grid lines -->
          <g class="grid-lines">
            <line x1="0" y1="30" x2="400" y2="30" stroke="#f1f5f9" stroke-width="1" />
            <line x1="0" y1="60" x2="400" y2="60" stroke="#f1f5f9" stroke-width="1" />
            <line x1="0" y1="90" x2="400" y2="90" stroke="#f1f5f9" stroke-width="1" />
          </g>

          <!-- Gradient definition -->
          <defs>
            <linearGradient :id="'gradient-' + chartId" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stop-color="#8b5cf6" stop-opacity="0.3" />
              <stop offset="100%" stop-color="#8b5cf6" stop-opacity="0.02" />
            </linearGradient>
          </defs>

          <!-- Area fill -->
          <path
            :d="areaPath"
            :fill="`url(#gradient-${chartId})`"
          />

          <!-- Line -->
          <path
            :d="linePath"
            fill="none"
            stroke="#8b5cf6"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />

          <!-- Data points -->
          <g class="data-points">
            <circle
              v-for="(point, index) in chartPoints"
              :key="index"
              :cx="point.x"
              :cy="point.y"
              r="4"
              fill="#8b5cf6"
              stroke="white"
              stroke-width="2"
              class="data-point"
            />
          </g>
        </svg>

        <!-- X-axis labels -->
        <div class="x-axis-labels">
          <span v-for="(label, index) in xAxisLabels" :key="index">{{ label }}</span>
        </div>
      </div>
    </div>

    <!-- Recent Campaigns Section -->
    <div v-if="recentCampaigns.length > 0" class="campaigns-section">
      <div class="section-header">
        <span class="section-title">Recent Campaigns</span>
        <router-link :to="{ name: 'campaigns' }" class="view-all-link">
          View All <i class="fa fa-chevron-right"></i>
        </router-link>
      </div>
      <div class="campaigns-list">
        <!-- Real campaign data -->
        <template v-if="!isShowingSampleData">
          <div
            v-for="campaign in recentCampaigns"
            :key="campaign.CampaignID"
            class="campaign-item"
            @click="goToCampaign(campaign)"
          >
            <div class="campaign-avatar" :class="getStatusClass(campaign.Status)">
              <i :class="getChannelIcon(campaign.Channel)"></i>
            </div>
            <div class="campaign-content">
              <div class="campaign-header">
                <span class="campaign-name">{{ campaign.Name }}</span>
                <span class="campaign-badge" :class="getStatusClass(campaign.Status)">
                  {{ campaign.Status }}
                </span>
              </div>
              <p class="campaign-meta">
                {{ campaign.RecipientCount || 0 }} recipients
                <span v-if="campaign.SentAt"> &bull; Sent {{ getTimeAgo(campaign.SentAt) }}</span>
              </p>
            </div>
          </div>
        </template>
        <!-- Sample campaign data -->
        <template v-else>
          <div
            v-for="(item, idx) in recentCampaigns"
            :key="idx"
            class="campaign-item"
            :class="{ clickable: item.clickable }"
            @click="handleSampleItemClick(item)"
          >
            <div class="campaign-avatar" :class="item.avatarClass || 'status-active'">
              <i :class="item.icon || 'fa fa-bullhorn'"></i>
            </div>
            <div class="campaign-content">
              <div class="campaign-header">
                <span class="campaign-name">{{ item.name }}</span>
                <span v-if="item.badge" class="campaign-badge" :class="item.badgeClass">
                  {{ item.badge }}
                </span>
              </div>
              <p class="campaign-meta">{{ item.meta }}</p>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!isLoadingCampaigns" class="campaigns-empty">
      <i class="fa fa-bullhorn"></i>
      <span>No recent campaigns</span>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import moment from 'moment-timezone';

export default {
  name: 'CampaignsTrendChart',

  props: {
    chartData: {
      type: Array,
      default: () => [5, 12, 8, 15, 10, 18, 14]
    },
    labels: {
      type: Array,
      default: () => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    },
    totalSent: {
      type: [String, Number],
      default: null
    },
    trend: {
      type: Number,
      default: null
    },
    maxCampaigns: {
      type: Number,
      default: 3
    },
    // Sample recent campaigns data (used when no real data is available)
    sampleRecentCampaigns: {
      type: Array,
      default: () => []
      // Format: [{ name, meta, icon, badge, badgeClass, clickable }]
    }
  },

  data() {
    return {
      chartId: Math.random().toString(36).substr(2, 9),
      selectedPeriod: 'week',
      periods: [
        { label: 'Day', value: 'day' },
        { label: 'Week', value: 'week' },
        { label: 'Month', value: 'month' }
      ],
      isLoadingCampaigns: true
    };
  },

  computed: {
    ...mapState('campaigns', ['campaigns']),
    ...mapGetters('campaigns', ['activeCampaigns', 'draftCampaigns']),

    xAxisLabels() {
      return this.labels;
    },

    totalSentThisWeek() {
      if (this.totalSent !== null) return this.totalSent;
      return this.chartData.reduce((a, b) => a + b, 0);
    },

    weeklyTrend() {
      return this.trend;
    },

    activeCampaignsCount() {
      return this.activeCampaigns ? this.activeCampaigns.filter(c => c.Status === 'Active').length : 0;
    },

    scheduledCampaignsCount() {
      return this.activeCampaigns ? this.activeCampaigns.filter(c => c.Status === 'Scheduled').length : 0;
    },

    draftCampaignsCount() {
      return this.draftCampaigns ? this.draftCampaigns.length : 0;
    },

    chartPoints() {
      const data = this.chartData;
      if (!data || data.length === 0) return [];

      const width = 400;
      const height = 120;
      const padding = { top: 10, bottom: 20, left: 10, right: 10 };
      const chartWidth = width - padding.left - padding.right;
      const chartHeight = height - padding.top - padding.bottom;

      const max = Math.max(...data) || 1;
      const min = 0;

      return data.map((val, i) => ({
        x: padding.left + (i / (data.length - 1)) * chartWidth,
        y: padding.top + chartHeight - ((val - min) / (max - min)) * chartHeight
      }));
    },

    linePath() {
      const points = this.chartPoints;
      if (points.length === 0) return '';

      return points.reduce((path, point, i) => {
        if (i === 0) return `M ${point.x},${point.y}`;
        return `${path} L ${point.x},${point.y}`;
      }, '');
    },

    areaPath() {
      const points = this.chartPoints;
      if (points.length === 0) return '';

      const height = 120;
      const padding = { bottom: 20, left: 10, right: 10 };
      const width = 400;

      let path = `M ${padding.left},${height - padding.bottom}`;
      points.forEach((point) => {
        path += ` L ${point.x},${point.y}`;
      });
      path += ` L ${width - padding.right},${height - padding.bottom}`;
      path += ' Z';

      return path;
    },

    recentCampaigns() {
      if (!this.campaigns || this.campaigns.length === 0) {
        // Return sample data if no real campaigns
        return this.sampleRecentCampaigns.slice(0, this.maxCampaigns);
      }
      return [...this.campaigns]
        .sort((a, b) => {
          const dateA = new Date(a.SentAt || a.CreatedAt || 0);
          const dateB = new Date(b.SentAt || b.CreatedAt || 0);
          return dateB - dateA;
        })
        .slice(0, this.maxCampaigns);
    },

    // Check if we're showing sample data vs real data
    isShowingSampleData() {
      return !this.campaigns || this.campaigns.length === 0;
    }
  },

  watch: {
    selectedPeriod(newPeriod) {
      this.$emit('period-change', newPeriod);
    }
  },

  async mounted() {
    await this.loadCampaigns();
  },

  methods: {
    ...mapActions('campaigns', ['fetchCampaigns']),

    async loadCampaigns() {
      this.isLoadingCampaigns = true;
      try {
        await this.fetchCampaigns();
      } catch (error) {
        console.error('Error loading campaigns:', error);
      } finally {
        this.isLoadingCampaigns = false;
      }
    },

    goToCampaign(campaign) {
      this.$router.push({ name: 'CampaignDetails', params: { id: campaign.CampaignID } });
    },

    handleSampleItemClick(item) {
      if (item.clickable) {
        this.$emit('item-click', item);
      }
    },

    getTimeAgo(date) {
      if (!date) return '';
      return moment(date).fromNow();
    },

    getStatusClass(status) {
      switch (status) {
        case 'Active':
          return 'status-active';
        case 'Scheduled':
          return 'status-scheduled';
        case 'Completed':
          return 'status-completed';
        case 'Draft':
        default:
          return 'status-draft';
      }
    },

    getChannelIcon(channel) {
      switch (channel) {
        case 'SMS':
          return 'fa fa-mobile';
        case 'Email':
          return 'fa fa-envelope';
        case 'Both':
          return 'fa fa-paper-plane';
        default:
          return 'fa fa-bullhorn';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.campaigns-trend-chart {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

// Chart Header
.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px 0;
}

.chart-title-section {
  flex: 1;
}

.chart-title {
  font-size: 1.0625rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.chart-subtitle {
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0;
}

.chart-controls {
  flex-shrink: 0;
}

.period-tabs {
  display: flex;
  background-color: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}

.period-tab {
  padding: 6px 14px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #334155;
  }

  &.active {
    background: white;
    color: #8b5cf6;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
}

// Chart Body
.chart-body {
  padding: 16px 24px 16px;
}

.chart-top-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.chart-summary {
  display: inline-block;
}

.summary-main {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
}

.summary-trend {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 14px;

  i {
    font-size: 0.6875rem;
  }

  &.trend-up {
    background-color: rgba(34, 197, 94, 0.1);
    color: #16a34a;
  }

  &.trend-down {
    background-color: rgba(239, 68, 68, 0.1);
    color: #dc2626;
  }
}

.summary-label {
  font-size: 0.875rem;
  color: #64748b;
}

// Inline Stats
.inline-stats {
  display: flex;
  gap: 24px;
}

.inline-stat {
  text-align: center;

  .inline-stat-value {
    display: block;
    font-size: 1.25rem;
    font-weight: 700;
    color: #334155;
    line-height: 1.2;
  }

  .inline-stat-label {
    font-size: 0.6875rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

// Chart Container
.chart-container {
  position: relative;
  width: 100%;
}

.area-chart {
  width: 100%;
  height: 140px;
  display: block;
}

.data-point {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.area-chart:hover .data-point {
  opacity: 1;
}

.x-axis-labels {
  display: flex;
  justify-content: space-between;
  padding: 8px 10px 0;

  span {
    font-size: 0.6875rem;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

// Campaigns Section
.campaigns-section {
  border-top: 1px solid #e8ecf1;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 24px 10px;
}

.section-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.view-all-link {
  font-size: 0.8125rem;
  color: #8b5cf6;
  text-decoration: none;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s ease;

  i {
    font-size: 0.625rem;
  }

  &:hover {
    color: #7c3aed;
  }
}

.campaigns-list {
  padding: 0 0 8px;
}

.campaign-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 24px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f8fafc;
  }
}

.campaign-avatar {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &.status-active {
    background-color: rgba(34, 197, 94, 0.1);
    color: #22c55e;
  }

  &.status-scheduled {
    background-color: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  }

  &.status-completed {
    background-color: rgba(100, 116, 139, 0.1);
    color: #64748b;
  }

  &.status-draft {
    background-color: rgba(251, 191, 36, 0.1);
    color: #f59e0b;
  }

  i {
    font-size: 1rem;
  }
}

.campaign-content {
  flex: 1;
  min-width: 0;
}

.campaign-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 2px;
}

.campaign-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.campaign-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.6875rem;
  font-weight: 500;
  flex-shrink: 0;

  &.status-active {
    background-color: rgba(34, 197, 94, 0.1);
    color: #22c55e;
  }

  &.status-scheduled {
    background-color: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  }

  &.status-completed {
    background-color: rgba(100, 116, 139, 0.1);
    color: #64748b;
  }

  &.status-draft {
    background-color: rgba(251, 191, 36, 0.1);
    color: #f59e0b;
  }
}

.campaign-meta {
  font-size: 0.75rem;
  color: #64748b;
  margin: 0;
}

// Empty State
.campaigns-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 24px;
  border-top: 1px solid #e8ecf1;
  color: #94a3b8;
  gap: 8px;

  i {
    font-size: 1.25rem;
    opacity: 0.6;
  }

  span {
    font-size: 0.8125rem;
  }
}
</style>
